import { apiClient } from './client'

/**
 * LGPD Block D — Data Subject Rights API client.
 *
 * Thin wrappers over the `/api/v1/user` endpoints. They go through `apiClient`,
 * inheriting the BFF strategy: `credentials: 'include'` (HttpOnly session cookie)
 * and the automatic `X-CSRF-Token` double-submit on the POST/PATCH calls.
 *
 * All endpoints require an authenticated session. The deletion request is
 * NON-DESTRUCTIVE: it records a request for manual review and deletes nothing.
 */

// snake_case to match the backend (auth/identity domain shape).

export interface UserProfileDTO {
  id: string
  email: string
  full_name: string
  display_name: string | null
  avatar_url: string | null
  status: string
  created_at: string
  updated_at: string
}

export interface PreferencesDTO {
  theme: string | null
  density: string | null
  locale: string | null
}

export interface MembershipDTO {
  organization_id: string
  organization_name: string
  organization_slug: string
  tenant_id: string
  role: string
  member_status: string
  created_at: string | null
}

export interface ConsentStateDataDTO {
  action: string
  policy_version: string
  categories: { essential: boolean; analytics: boolean; marketing: boolean }
  source: string | null
  created_at: string | null
}

export interface DocumentMetadataDTO {
  id: string
  document_id: string
  filename: string | null
  title: string | null
  content_type: string | null
  file_size_bytes: number | null
  status: string | null
  document_kind: string | null
  reference_type: string | null
  reference_domain: string | null
  created_at: string | null
  updated_at: string | null
  indexed_at: string | null
}

export interface CollectionSummaryDTO<T> {
  total: number
  truncated: boolean
  items: T[]
}

export interface ConversationMetadataDTO {
  id: string
  title: string | null
  archived: boolean
  pinned: boolean
  created_at: string | null
  updated_at: string | null
}

export interface SecuritySummaryDTO {
  active_sessions: number
  last_seen_at: string | null
  last_session_created_at: string | null
}

export interface UserDataDTO {
  user: UserProfileDTO
  preferences: PreferencesDTO | null
  memberships: MembershipDTO[]
  consent: ConsentStateDataDTO | null
  policy_version: string
  documents: CollectionSummaryDTO<DocumentMetadataDTO>
  conversations: CollectionSummaryDTO<ConversationMetadataDTO>
  security: SecuritySummaryDTO
}

export interface UserDataExportDTO {
  export_generated_at: string
  format_version: string
  scope: { included: string[]; excluded: string[] }
  data: UserDataDTO
  consent_history: ConsentStateDataDTO[]
  data_requests: { id: string; request_type: string; status: string; created_at: string }[]
}

export interface UserDataCorrectionPayload {
  full_name?: string
  display_name?: string | null
  locale?: string | null
  theme?: string | null
  density?: string | null
}

export interface UserDataCorrectionDTO {
  user: UserProfileDTO
  preferences: PreferencesDTO | null
  updated_fields: string[]
}

export interface SubprocessorDTO {
  key: string
  name: string
  active: boolean
  purpose: string
  data_categories: string[]
  international_transfer: 'yes' | 'no' | 'unknown'
  notes: string | null
}

export interface SharingDTO {
  generated_at: string
  subprocessors: SubprocessorDTO[]
  notes: string
}

export interface DeleteRequestDTO {
  id: string
  request_type: string
  status: string
  created_at: string
  destructive: boolean
  manual_review_required: boolean
  deletion_cascade_planned: boolean
  message: string
}

export const userDataApi = {
  /** Safe summary of the current user's personal data. */
  getData: (signal?: AbortSignal) =>
    apiClient.get<UserDataDTO>('/api/v1/user/data', undefined, signal),

  /** Machine-readable JSON export (CSRF handled by the client). */
  export: () => apiClient.post<UserDataExportDTO>('/api/v1/user/export'),

  /** Correct only safe profile/preferences fields (CSRF handled). */
  correct: (payload: UserDataCorrectionPayload) =>
    apiClient.patch<UserDataCorrectionDTO>('/api/v1/user/data', payload),

  /** Subprocessors / data-sharing info. */
  getSharing: (signal?: AbortSignal) =>
    apiClient.get<SharingDTO>('/api/v1/user/data/sharing', undefined, signal),

  /** Record a NON-DESTRUCTIVE deletion request (CSRF handled). */
  requestDeletion: (reason?: string) =>
    apiClient.post<DeleteRequestDTO>(
      '/api/v1/user/delete-request',
      reason ? { reason } : {},
    ),
}
